import { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Button, Card, CardContent, Chip, Stack, CircularProgress, Alert, Snackbar, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Plus, Calendar, Target, Gift, ChevronDown, Archive } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import { getFullUserData, getUserBusinessName } from '../utils/userHelper';
import { getCampaigns } from '../services/campaignService';
import type { CampaignData } from '../types/campaign';

const PageContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  bgcolor: '#F9FAFB'
});

const MainContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const ContentArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4)
}));

const StyledCard = styled(Card)(({ theme }) => ({
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  boxShadow: 'none',
  cursor: 'pointer',
  transition: 'all 0.2s',
  backgroundColor: '#FFFFFF',
  '&:hover': {
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-2px)',
    borderColor: '#D1D5DB'
  }
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(8),
  textAlign: 'center'
}));

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return { bgcolor: '#ECFDF5', color: '#059669' }; // Green
    case 'draft':
      return { bgcolor: '#F3F4F6', color: '#6B7280' }; // Gray
    case 'completed':
      return { bgcolor: '#EEF2FF', color: '#4F46E5' }; // Indigo
    case 'paused':
      return { bgcolor: '#FEF3C7', color: '#D97706' }; // Yellow
    case 'closed':
      return { bgcolor: '#F3F4F6', color: '#6B7280' }; // Gray
    case 'ended':
      return { bgcolor: '#F3F4F6', color: '#6B7280' }; // Gray
    default:
      return { bgcolor: '#F3F4F6', color: '#6B7280' }; // Gray
  }
};

// Helper function to check if campaign is closed/ended
const isClosedCampaign = (status: string | undefined): boolean => {
  if (!status) return false;
  const statusLower = status.toLowerCase().trim();
  const closedStatuses = ['completed', 'closed', 'ended', 'finish', 'finished', 'done'];
  return closedStatuses.includes(statusLower);
};

// Helper function to get reward display text
const getRewardDisplay = (campaign: CampaignData): string => {
  switch (campaign.reward_type?.toLowerCase()) {
    case 'coupon':
      if (campaign.coupon_discount_percent) {
        return `Coupon: ${campaign.coupon_discount_percent}% off`;
      }
      return 'Coupon Reward';
    case 'cash':
      if (campaign.cash_amount && campaign.cash_currency) {
        return `Cash: ${campaign.cash_currency} ${campaign.cash_amount}`;
      }
      return 'Cash Reward';
    case 'none':
      return 'No Reward';
    default:
      return 'No Reward';
  }
};

const Campaigns = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = getFullUserData();
  const businessName = getUserBusinessName();
  const [campaigns, setCampaigns] = useState<CampaignData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Show success message from navigation state
  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      // Clear the message from location state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Fetch campaigns on component mount
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch more campaigns to ensure we get closed/ended ones
        const response = await getCampaigns(100, 0);
        
        if (response.success && response.data?.campaigns) {
          setCampaigns(response.data.campaigns);
          console.log('Campaigns loaded:', response.data.campaigns.length);
          // Debug: Log all campaign statuses
          const statuses = response.data.campaigns.map((c: CampaignData) => c.status);
          console.log('Campaign statuses:', statuses);
          const uniqueStatuses = [...new Set(statuses)];
          console.log('Unique statuses:', uniqueStatuses);
        } else {
          throw new Error(response.message || 'Failed to fetch campaigns');
        }
      } catch (err) {
        console.error('Error fetching campaigns:', err);
        setError(err instanceof Error ? err.message : 'Failed to load campaigns');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  const handleCreateCampaign = () => {
    navigate('/campaigns/create');
  };

  const handleCampaignClick = (campaignId: string) => {
    navigate(`/campaigns/${campaignId}`);
  };

  // Separate active and closed campaigns
  const { activeCampaigns, closedCampaigns } = useMemo(() => {
    const active: CampaignData[] = [];
    const closed: CampaignData[] = [];

    campaigns.forEach((campaign) => {
      if (isClosedCampaign(campaign.status)) {
        closed.push(campaign);
      } else {
        active.push(campaign);
      }
    });

    console.log('Active campaigns:', active.length);
    console.log('Closed campaigns:', closed.length);
    console.log('Closed campaign statuses:', closed.map(c => c.status));

    return { activeCampaigns: active, closedCampaigns: closed };
  }, [campaigns]);

  return (
    <PageContainer>
      <Sidebar user={userData} />
      
      <MainContent>
        <DashboardHeader
          businessName={businessName}
          userAvatar={userData.avatar}
          businessLogo={userData.businessLogo}
        />
        
        <ContentArea>
          {/* Page Heading */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 4,
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.875rem', md: '2.25rem' },
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  color: '#111827',
                  mb: 0.5
                }}
              >
                Campaigns
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#6B7280',
                  fontSize: '0.875rem',
                  fontFamily: '"Inter", sans-serif'
                }}
              >
                Manage your review campaigns and track performance
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<Plus size={20} />}
              onClick={handleCreateCampaign}
              sx={{
                height: 44,
                backgroundColor: '#4F46E5',
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                px: 3,
                borderRadius: '8px',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#4338CA',
                  boxShadow: 'none'
                }
              }}
            >
              Create Campaign
            </Button>
          </Box>

          {/* Loading State */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress size={40} />
            </Box>
          ) : activeCampaigns.length > 0 || closedCampaigns.length > 0 ? (
            <>
              {/* Active Campaigns */}
              {activeCampaigns.length > 0 && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                    gap: 3,
                    mb: closedCampaigns.length > 0 ? 4 : 0
                  }}
                >
                  {activeCampaigns.map((campaign) => (
                <StyledCard
                  key={campaign.id}
                  onClick={() => handleCampaignClick(campaign.id)}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start'
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            fontSize: '1rem',
                            mb: 0.5,
                            color: '#111827',
                            fontFamily: '"Inter", sans-serif'
                          }}
                        >
                          {campaign.campaign_name}
                        </Typography>
                        <Chip
                          label={campaign.status || 'active'}
                          size="small"
                          sx={{
                            textTransform: 'capitalize',
                            ...getStatusColor(campaign.status || 'active'),
                            fontWeight: 600,
                            fontSize: '0.75rem'
                          }}
                        />
                      </Box>

                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Target size={16} color="#6B7280" />
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', fontSize: '0.875rem', textTransform: 'capitalize' }}
                          >
                            {campaign.platform}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Gift size={16} color="#6B7280" />
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                          >
                            {getRewardDisplay(campaign)}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Calendar size={16} color="#6B7280" />
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                          >
                            Created {campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </CardContent>
                </StyledCard>
                  ))}
                </Box>
              )}

              {/* Closed/Ended Campaigns Accordion */}
              <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid #E5E7EB' }}>
                  <Accordion
                    defaultExpanded={false}
                    sx={{
                      backgroundColor: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '12px',
                      boxShadow: 'none',
                      '&:before': {
                        display: 'none'
                      },
                      '&.Mui-expanded': {
                        margin: 0,
                        backgroundColor: '#FFFFFF'
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            backgroundColor: '#FFFFFF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #E5E7EB'
                          }}
                        >
                          <ChevronDown size={18} color="#6B7280" />
                        </Box>
                      }
                      sx={{
                        px: 3,
                        py: 2.5,
                        '&.Mui-expanded': {
                          minHeight: 64,
                          borderBottom: '1px solid #E5E7EB',
                          backgroundColor: '#FFFFFF'
                        },
                        '& .MuiAccordionSummary-content': {
                          my: 0,
                          alignItems: 'center'
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            backgroundColor: '#EEF2FF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Archive size={20} color="#4F46E5" />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontFamily: '"Inter", sans-serif',
                              fontWeight: 600,
                              fontSize: '0.9375rem',
                              color: '#111827',
                              mb: 0.5
                            }}
                          >
                            Closed / Ended Campaigns
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6B7280',
                              fontSize: '0.75rem',
                              fontFamily: '"Inter", sans-serif'
                            }}
                          >
                            {closedCampaigns.length > 0 
                              ? `${closedCampaigns.length} ${closedCampaigns.length === 1 ? 'campaign' : 'campaigns'} archived`
                              : 'No archived campaigns'}
                          </Typography>
                        </Box>
                        {closedCampaigns.length > 0 && (
                          <Chip
                            label={closedCampaigns.length}
                            size="small"
                            sx={{
                              bgcolor: '#EEF2FF',
                              color: '#4F46E5',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              height: 28,
                              minWidth: 40
                            }}
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 3, backgroundColor: '#FFFFFF' }}>
                      {closedCampaigns.length > 0 ? (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                            gap: 3
                          }}
                        >
                          {closedCampaigns.map((campaign) => (
                          <StyledCard
                            key={campaign.id}
                            onClick={() => handleCampaignClick(campaign.id)}
                            sx={{
                              borderColor: '#E5E7EB',
                              backgroundColor: '#FAFAFA',
                              '&:hover': {
                                backgroundColor: '#FFFFFF',
                                borderColor: '#D1D5DB',
                                transform: 'translateY(-2px)'
                              }
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              <Stack spacing={2}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start'
                                  }}
                                >
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '1rem',
                                      mb: 0.5,
                                      color: '#111827',
                                      fontFamily: '"Inter", sans-serif'
                                    }}
                                  >
                                    {campaign.campaign_name}
                                  </Typography>
                                  <Chip
                                    label={campaign.status || 'completed'}
                                    size="small"
                                    sx={{
                                      textTransform: 'capitalize',
                                      ...getStatusColor(campaign.status || 'completed'),
                                      fontWeight: 600,
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                </Box>

                                <Stack spacing={1.5}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Target size={16} color="#6B7280" />
                                    <Typography
                                      variant="body2"
                                      sx={{ color: 'text.secondary', fontSize: '0.875rem', textTransform: 'capitalize' }}
                                    >
                                      {campaign.platform}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Gift size={16} color="#6B7280" />
                                    <Typography
                                      variant="body2"
                                      sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                                    >
                                      {getRewardDisplay(campaign)}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Calendar size={16} color="#6B7280" />
                                    <Typography
                                      variant="body2"
                                      sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                                    >
                                      Created {campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : 'N/A'}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Stack>
                            </CardContent>
                          </StyledCard>
                          ))}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            py: 6,
                            textAlign: 'center',
                            backgroundColor: '#F9FAFB',
                            borderRadius: '8px',
                            border: '1px dashed #E5E7EB'
                          }}
                        >
                          <Archive size={32} color="#9CA3AF" style={{ marginBottom: '12px', opacity: 0.5 }} />
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#6B7280',
                              fontSize: '0.875rem',
                              fontFamily: '"Inter", sans-serif'
                            }}
                          >
                            No closed or ended campaigns
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#9CA3AF',
                              fontSize: '0.75rem',
                              fontFamily: '"Inter", sans-serif',
                              mt: 0.5,
                              display: 'block'
                            }}
                          >
                            Completed, closed, or ended campaigns will appear here
                          </Typography>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Box>
            </>
          ) : (
            <Card
              sx={{
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                boxShadow: 'none',
                backgroundColor: '#FFFFFF'
              }}
            >
              <EmptyState>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: '#EEF2FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3
                  }}
                >
                  <Target size={40} color="#4F46E5" />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: '#111827',
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '1.5rem'
                  }}
                >
                  No campaigns yet
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    maxWidth: 400,
                    color: '#6B7280',
                    fontSize: '0.875rem'
                  }}
                >
                  Create your first campaign to start collecting reviews and feedback from your customers.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Plus size={20} />}
                  onClick={handleCreateCampaign}
                  sx={{
                    height: 44,
                    backgroundColor: '#4F46E5',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    px: 3,
                    borderRadius: '8px',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#4338CA',
                      boxShadow: 'none'
                    }
                  }}
                >
                  Create Your First Campaign
                </Button>
              </EmptyState>
            </Card>
          )}

          {/* Error Snackbar */}
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Snackbar>

          {/* Success Snackbar */}
          <Snackbar
            open={!!success}
            autoHideDuration={4000}
            onClose={() => setSuccess(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
              {success}
            </Alert>
          </Snackbar>
        </ContentArea>
      </MainContent>
    </PageContainer>
  );
};

export default Campaigns;

